import { useLocation } from 'react-router-dom'
import { LoaderCircle, TriangleAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import type { ImageGenerationDraftSession } from '@/features/image-generation'
import { useDefaultTopUpOfferCode } from '@/features/pricing/useRuntimePricing'
import { useSubscriptionStore } from '@/stores/subscriptionStore'

const TEXTAREA_CLASS_NAME =
  'flex min-h-[17rem] w-full rounded-[24px] border border-input bg-background/95 px-4 py-4 text-sm leading-6 ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'

type ImagePromptDialogProps = {
  onGenerate: () => Promise<void> | void
  onOpenChange: (open: boolean) => void
  onPromptChange: (prompt: string) => void
  open: boolean
  session: ImageGenerationDraftSession
}

function getTargetLabel(
  session: ImageGenerationDraftSession,
  t: (key: string, options?: Record<string, unknown>) => string,
) {
  switch (session.sourceKind) {
    case 'chapter':
      return t('imageGeneration.targets.chapter')
    case 'side_story':
      return t('imageGeneration.targets.sideStory')
    case 'timeline_event':
      return t('imageGeneration.targets.timelineEvent')
    case 'lore_entry':
      return t('imageGeneration.targets.lore')
    case 'entity_card':
      return t('imageGeneration.targets.entity')
  }
}

function getMinutesUntilRetry(retryAfterSeconds: number | null | undefined) {
  if (typeof retryAfterSeconds !== 'number' || !Number.isFinite(retryAfterSeconds)) {
    return 1
  }

  return Math.max(1, Math.ceil(retryAfterSeconds / 60))
}

function formatResumeDate(resetAt: string | null | undefined) {
  if (!resetAt) {
    return ''
  }

  const date = new Date(resetAt)
  if (Number.isNaN(date.getTime())) {
    return ''
  }

  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date)
}

export function ImagePromptDialog({
  onGenerate,
  onOpenChange,
  onPromptChange,
  open,
  session,
}: ImagePromptDialogProps) {
  const { t } = useTranslation()
  const location = useLocation()
  const checkout = useSubscriptionStore((state) => state.checkout)
  const defaultTopUpOfferCode = useDefaultTopUpOfferCode()
  const generateDisabled =
    session.requestStatus === 'loading'
    || session.editedPrompt.trim().length === 0
    || session.accessState.code !== 'available'
  const isGenerating = session.requestStatus === 'loading'
  const creditsRemaining = session.creditsRemaining ?? session.accessState.creditsRemaining
  const targetLabel = getTargetLabel(session, t)

  function handleAccessAction() {
    if (session.accessState.code === 'tier-locked') {
      void checkout(
        session.accessState.recommendedTier === 'author' ? 'author_monthly' : 'crafter_monthly',
        location.pathname,
      )
      return
    }

    if (session.accessState.code === 'insufficient-credits') {
      void checkout(defaultTopUpOfferCode, location.pathname)
    }
  }

  const accessSummary = (() => {
    switch (session.accessState.code) {
      case 'artstyle-required':
        return {
          body: t('imageGeneration.access.artDirectionRequiredBody'),
          detail: null,
          title: t('imageGeneration.access.artDirectionRequiredTitle'),
          tone: 'border-amber-300/40 bg-amber-50/85 text-amber-950',
        }
      case 'tier-locked':
        return {
          body: t('imageGeneration.access.upgradeRequiredBody', {
            tier: session.accessState.recommendedTier ?? 'crafter',
          }),
          detail: null,
          title: t('imageGeneration.access.upgradeRequiredTitle'),
          tone: 'border-sky-300/40 bg-sky-50/85 text-sky-950',
        }
      case 'insufficient-credits':
        return {
          body: t('imageGeneration.access.insufficientCreditsBody', {
            count: session.creditCost,
          }),
          detail: null,
          title: t('imageGeneration.access.insufficientCreditsTitle'),
          tone: 'border-amber-300/40 bg-amber-50/85 text-amber-950',
        }
      case 'rate-limit-reached':
        return {
          body: t('story.ai.access.rateLimitPrompt', {
            minutes: getMinutesUntilRetry(session.accessState.retryAfterSeconds),
          }),
          detail: session.accessState.creditsSafe
            ? t('story.ai.access.creditsSafe')
            : null,
          title: t('imageGeneration.access.temporarilyLimitedTitle'),
          tone: 'border-amber-300/40 bg-amber-50/85 text-amber-950',
        }
      case 'budget-restricted':
        return {
          body: t('story.ai.access.budgetRestrictedPrompt', {
            resumeDate: formatResumeDate(session.accessState.resetAt),
          }),
          detail: session.accessState.creditsSafe
            ? t('story.ai.access.creditsSafe')
            : null,
          title: t('imageGeneration.access.temporarilyLimitedTitle'),
          tone: 'border-amber-300/40 bg-amber-50/85 text-amber-950',
        }
      case 'service-busy':
        return {
          body: t('story.ai.access.serviceBusyPrompt'),
          detail: session.accessState.creditsSafe
            ? t('story.ai.access.creditsSafe')
            : null,
          title: t('imageGeneration.access.temporarilyLimitedTitle'),
          tone: 'border-amber-300/40 bg-amber-50/85 text-amber-950',
        }
      default:
        return {
          body: t('imageGeneration.access.readyBody'),
          detail: null,
          title: t('imageGeneration.access.readyTitle'),
          tone: 'border-emerald-300/40 bg-emerald-50/85 text-emerald-950',
        }
    }
  })()
  const aiGuardNotices = [
    session.accessState.cacheStatus === 'hit'
      ? t('story.ai.access.cacheHit')
      : null,
    session.accessState.truncated
      ? t('story.ai.access.truncationWarning')
      : null,
  ].filter((notice): notice is string => Boolean(notice))

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="w-[min(96vw,74rem)] gap-6">
        <DialogHeader>
          <div className="flex flex-wrap items-start justify-between gap-3 pr-10">
            <div className="space-y-2">
              <DialogTitle>
                {t('imageGeneration.promptDialog.title', { target: targetLabel })}
              </DialogTitle>
              <DialogDescription>
                {t('imageGeneration.promptDialog.description')}
              </DialogDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline">{session.providerLabel}</Badge>
              <Badge variant="outline">{session.modelName}</Badge>
              <Badge variant="outline">{targetLabel}</Badge>
              <Badge variant="outline">
                {t('imageGeneration.promptDialog.credits', { count: session.creditCost })}
              </Badge>
              {typeof creditsRemaining === 'number' ? (
                <Badge variant="outline">
                  {t('imageGeneration.promptDialog.remaining', { count: creditsRemaining })}
                </Badge>
              ) : null}
            </div>
          </div>
        </DialogHeader>

        <div className="grid gap-5 xl:grid-cols-[minmax(0,1.4fr)_minmax(19rem,0.8fr)]">
          <div className="space-y-3">
            <Label htmlFor="image-generation-prompt">
              {t('imageGeneration.promptDialog.promptLabel')}
            </Label>
            <textarea
              className={TEXTAREA_CLASS_NAME}
              id="image-generation-prompt"
              onChange={(event) => onPromptChange(event.target.value)}
              placeholder={t('imageGeneration.promptDialog.promptPlaceholder')}
              value={session.editedPrompt}
            />
            <div className={`rounded-2xl border px-4 py-3 text-sm ${accessSummary.tone}`}>
              <p className="font-semibold">{accessSummary.title}</p>
              <p className="mt-1 leading-6">{accessSummary.body}</p>
              {accessSummary.detail ? (
                <p className="mt-2 text-xs">{accessSummary.detail}</p>
              ) : null}
            </div>
            {aiGuardNotices.length > 0 ? (
              <div className="space-y-2 rounded-2xl border border-sky-300/35 bg-sky-50/70 px-4 py-3 text-sm text-sky-950">
                {aiGuardNotices.map((notice) => (
                  <p key={notice}>{notice}</p>
                ))}
              </div>
            ) : null}
            {isGenerating ? (
              <div
                className="flex items-start gap-3 rounded-2xl border border-border/70 bg-secondary/35 px-4 py-3 text-sm text-muted-foreground"
                role="status"
              >
                <LoaderCircle className="mt-0.5 h-4 w-4 animate-spin" />
                <p>
                  {t('imageGeneration.promptDialog.loading', {
                    provider: session.providerLabel,
                  })}
                </p>
              </div>
            ) : session.errorMessage ? (
              <div
                className="flex items-start gap-3 rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive"
                role="alert"
              >
                <TriangleAlert className="mt-0.5 h-4 w-4" />
                <div className="space-y-1">
                  <p className="font-semibold">
                    {t('imageGeneration.promptDialog.errorTitle')}
                  </p>
                  <p>{session.errorMessage}</p>
                  <p className="text-destructive/80">
                    {t('imageGeneration.promptDialog.errorRecovery')}
                  </p>
                </div>
              </div>
            ) : (
              <p className="rounded-2xl border border-dashed border-border/70 bg-secondary/30 px-4 py-3 text-sm text-muted-foreground">
                {t('imageGeneration.promptDialog.previewHint')}
              </p>
            )}
          </div>

          <div className="space-y-4">
            <section className="rounded-[26px] border border-border/70 bg-secondary/25 p-4">
              <div className="space-y-1">
                <p className="text-sm font-semibold text-foreground">
                  {t('imageGeneration.promptDialog.sourceContextTitle')}
                </p>
                <p className="text-sm text-muted-foreground">{session.sourceTitle}</p>
              </div>
              <div className="mt-4 space-y-3">
                {session.citations.map((citation) => (
                  <div
                    key={`${citation.sourceKind}:${citation.sourceRecordId}`}
                    className="rounded-[20px] border border-border/70 bg-white/80 px-3 py-3"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-semibold text-foreground">
                        {citation.sourceLabel}
                      </p>
                      <Badge variant="outline">
                        {t(`imageGeneration.sourceKinds.${citation.sourceKind}`)}
                      </Badge>
                      {citation.canonStatusLabel ? (
                        <Badge variant="outline">{citation.canonStatusLabel}</Badge>
                      ) : null}
                    </div>
                    <p className="mt-2 text-sm leading-6 text-muted-foreground">
                      {citation.reason}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-[26px] border border-border/70 bg-white/80 p-4">
              <div className="space-y-1">
                <p className="text-sm font-semibold text-foreground">
                  {t('imageGeneration.promptDialog.artstyleAnchorsTitle')}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t('imageGeneration.promptDialog.artstyleAnchorsBody')}
                </p>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {session.artstyleReferences.map((reference) => (
                  <Badge key={reference.id} variant="secondary">
                    {reference.fileName}
                  </Badge>
                ))}
              </div>
            </section>

            <section className="rounded-[26px] border border-border/70 bg-white/80 p-4">
              <p className="text-sm font-semibold text-foreground">
                {t('imageGeneration.promptDialog.promptBaselineTitle')}
              </p>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                {t('imageGeneration.promptDialog.promptBaselineBody')}
              </p>
            </section>
          </div>
        </div>

        <DialogFooter>
          {session.accessState.code === 'tier-locked' ? (
            <Button onClick={handleAccessAction} type="button" variant="outline">
              {t('imageGeneration.actions.upgradePlan')}
            </Button>
          ) : null}
          {session.accessState.code === 'insufficient-credits' ? (
            <Button onClick={handleAccessAction} type="button" variant="outline">
              {t('imageGeneration.actions.buyCredits')}
            </Button>
          ) : null}
          <Button onClick={() => onOpenChange(false)} type="button" variant="ghost">
            {t('common.cancel')}
          </Button>
          <Button
            disabled={generateDisabled}
            onClick={() => void onGenerate()}
            type="button"
          >
            {session.requestStatus === 'loading'
              ? t('imageGeneration.actions.generatingPreview')
              : t('imageGeneration.actions.generatePreview')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
